import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { fleet, fleetSpecOrder, type FleetSpecKey } from '../lib/fleet';

const specIcons: Record<FleetSpecKey, ReactNode> = {
  mtow: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 7.5V6.2A1.7 1.7 0 0 1 9.7 4.5h4.6A1.7 1.7 0 0 1 16 6.2v1.3" />
      <rect x="4.5" y="7.5" width="15" height="12" rx="1.6" />
      <path d="M4.5 12.5h15" />
    </svg>
  ),
  range: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="7.5" />
      <path d="M4.5 12h15M12 4.5c2.2 2.1 3.3 4.8 3.3 7.5s-1.1 5.4-3.3 7.5c-2.2-2.1-3.3-4.8-3.3-7.5s1.1-5.4 3.3-7.5z" />
    </svg>
  ),
  wingspan: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 14.5 3.5 11.2l1.2-1.4L12 12l7.3-2.2 1.2 1.4L12 14.5z" />
      <path d="M12 12v6.2M10.2 17.2h3.6" />
    </svg>
  ),
  length: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 13.5h10.2l2.4-3.2h3.2l1.2 2.1-2.2 1.1H3z" />
      <path d="M8.2 13.5v2.2M14.5 10.3v3.2" />
    </svg>
  ),
  height: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4.5v15" />
      <path d="M8.8 7.2 12 4.2l3.2 3M8.8 16.8 12 19.8l3.2-3" />
    </svg>
  ),
  passengers: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.5 7.2h2.4v2.4H6.5zM10.8 7.2h2.4v2.4h-2.4zM15.1 7.2h2.4v2.4h-2.4zM6.5 11.2h2.4v2.4H6.5zM10.8 11.2h2.4v2.4h-2.4zM15.1 11.2h2.4v2.4h-2.4zM6.5 15.2h2.4v2.4H6.5zM10.8 15.2h2.4v2.4h-2.4zM15.1 15.2h2.4v2.4h-2.4z" />
    </svg>
  ),
};

export function FleetPage() {
  const { t, i18n } = useTranslation();
  const numberLocale = i18n.language.startsWith('fa') ? 'fa-IR' : 'en-US';

  return (
    <main className="sa-page fleet-page">
      <div className="sa-container">
        <div className="fleet-list">
          {fleet.map((aircraft) => (
            <section key={aircraft.id} className="fleet-card" aria-labelledby={`fleet-${aircraft.id}`}>
              <h2 id={`fleet-${aircraft.id}`} className="fleet-card__name">
                {t(aircraft.nameKey)}
              </h2>
              <article className="fleet-card__stage">
                <aside className="fleet-card__specs">
                  <h3 className="fleet-card__specs-title">{t('fleet.specsTitle')}</h3>
                  <ul className="fleet-card__spec-list">
                    {fleetSpecOrder.map((spec) => (
                      <li key={spec.key} className="fleet-spec">
                        <span className="fleet-spec__label">
                          <span className="fleet-spec__icon">{specIcons[spec.key]}</span>
                          {t(`fleet.specs.${spec.key}`)}
                        </span>
                        <span className="fleet-spec__value">
                          {aircraft.specs[spec.key].toLocaleString(numberLocale, {
                            minimumFractionDigits: spec.digits,
                            maximumFractionDigits: spec.digits,
                          })}{' '}
                          {t(`fleet.units.${spec.unit}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </aside>
                <img
                  className="fleet-card__photo"
                  src={aircraft.image}
                  alt={t(aircraft.nameKey)}
                  width={1920}
                  height={1080}
                />
              </article>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
